import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import ProjectCard from '../components/ProjectCard';
import { getProjectThumbnail } from '../utils/screenshotCapture';
import Icon from '../components/icons/Icon';
import './Discover.css';

/**
 * Discover Page Component
 * Showcases a random selection of projects in a visually appealing gallery
 */
function Discover() {
  const navigate = useNavigate();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(() => miaFlowAuth.getSelectedTenant());

  // Fetch and randomly select 5 projects
  const fetchFeaturedProjects = useCallback(async () => {
    try {
      setLoading(true);
      
      const result = await miaPlatformDataService.getProjects(
        selectedTenant?.tenantId,
        null,
        1,
        50
      );
      
      const allProjects = result.data || [];

      // Randomly select 6 projects
      const shuffled = [...allProjects].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, 6);
      
      // Set projects immediately
      setFeaturedProjects(selected);
      setLoading(false);
      
      // Fetch thumbnails in the background
      selected.forEach(async (project) => {
        try {
          const thumbnail = await getProjectThumbnail(project._id, selectedTenant?.tenantId);
          if (thumbnail) {
            setFeaturedProjects(prevProjects => 
              prevProjects.map(p => 
                p._id === project._id ? { ...p, thumbnail } : p
              )
            );
          }
        } catch (error) {
          // Silently ignore thumbnail errors
        }
      });
    } catch (err) {
      console.error('Error fetching featured projects:', err);
      setLoading(false);
    }
  }, [selectedTenant?.tenantId]);

  useEffect(() => {
    fetchFeaturedProjects();
  }, [fetchFeaturedProjects]);

  // Listen for tenant changes
  useEffect(() => {
    const handleTenantChange = (event) => {
      setSelectedTenant(event.detail.tenant);
    };
    
    window.addEventListener('mia-flow-tenant-changed', handleTenantChange);
    
    return () => {
      window.removeEventListener('mia-flow-tenant-changed', handleTenantChange);
    };
  }, []);

  const handleProjectClick = (project) => {
    navigate(`/code/${project._id}`);
  };

  const handleViewAllProjects = () => {
    navigate('/projects');
  };

  const handleRefresh = () => {
    fetchFeaturedProjects();
  };

  return (
    <div className="discover-page">
      {/* Hero Section */}
      <div className="discover-hero">
        <div className="discover-hero-content">
          <div className="discover-icon">
            <Icon name="compass" size={64} />
          </div>
          <h1 className="discover-title">Discover Amazing Projects</h1>
          <p className="discover-subtitle">
            Explore innovative projects from the community and get inspired for your next creation
          </p>
        </div>
      </div>

      {/* Featured Projects Gallery */}
      <div className="discover-content">
        <div className="discover-section">
          <div className="section-header">
            <div className="section-header-content">
              <div className="section-header-text">
                <h2 className="section-title">Featured Projects</h2>
                <p className="section-description">A curated selection of standout projects</p>
              </div>
              <button className="refresh-button" onClick={handleRefresh} disabled={loading}>
                <Icon name="refresh-cw" size={20} />
                <span>Show me more!</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="discover-loading">
              <div className="loading-spinner"></div>
              <p>Loading featured projects...</p>
            </div>
          ) : featuredProjects.length === 0 ? (
            <div className="discover-empty">
              <Icon name="folder" size={64} className="empty-icon" />
              <h3>No projects yet</h3>
              <p>Start creating projects to see them featured here</p>
            </div>
          ) : (
            <div className="featured-projects-gallery">
              {featuredProjects.map((project, index) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  onClick={() => handleProjectClick(project)}
                  hideFavorite={true}
                />
              ))}
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="discover-cta">
          <div className="cta-card">
            <div className="cta-icon">
              <Icon name="folder" size={48} />
            </div>
            <h2 className="cta-title">Want to see more?</h2>
            <p className="cta-description">
              Browse all projects and find even more inspiration for your next big idea
            </p>
            <button 
              className="cta-button"
              onClick={handleViewAllProjects}
            >
              <Icon name="grid" size={20} />
              View All Projects
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Discover;
